/**
 * This module contains function to easily read values from the form.
 * Kind of a model layer for the form, of sorts. The helpers build the name of the field
 * to follow what's been set on the Rails server and allow differenciating between fields
 * present both for gas and electricity
 * @module embed/form
 */

import { getSubmissionData } from '@/embed/submission_data';
import { ATTR_HAS_END_DATE } from './tariff_list';
import { selectedOption } from '@/lib/forms';

const NAME_PREFIX = 'submission[submittable_attributes]';

function attributeNameWithFuelType(attributeName, fuelType) {
  return fuelType ? `${fuelType}_${attributeName}` : attributeName;
}

export function nameSelector(attributeName, fuelType) {
  return `[name="${NAME_PREFIX}[${attributeNameWithFuelType(
    attributeName,
    fuelType
  )}]"]`;
}

export function hasGas(scope) {
  return booleanRadioValue(scope, 'fuel_type', {
    trueValue: 'dual'
  });
}

export function hasDualFuel(scope) {
  if (getSwitchFuelType(scope) == 'dual') {
    return booleanRadioValue(scope, 'dual_fuel');
  }
}

export function isActualUsageInput(scope, fuelType) {
  return booleanRadioValue(scope, 'current_usage_preset', {
    fuelType,
    trueValue: 'actual'
  });
}

export function getEstimationType(scope, fuelType) {
  return radioValue(scope, 'current_estimation_type', { fuelType });
}

export function getSwitchFuelType(scope) {
  return hasGas(scope) ? 'dual' : 'electricity';
}

export function getPostcode(scope) {
  return controlValue(scope, 'postcode');
}

export function getPayMethod(scope, fuelType) {
  return radioValue(scope, 'current_pay_method', { fuelType });
}

export function hasTariffEndDate(scope, fuelType) {
  const select = scope.querySelector(nameSelector('tariff_id', fuelType));
  if (select) {
    const option = selectedOption(select);
    return option && option.getAttribute(ATTR_HAS_END_DATE) == 'true';
  }
}

export function getElectricityUsage(scope, { annual = false }) {
  return getUsage(scope, { fuelType: 'electricity', annual });
}

export function getGasUsage(scope, { annual = false }) {
  return getUsage(scope, { fuelType: 'gas', annual });
}

function getUsage(scope, { fuelType, annual = false }) {
  const hasElectricityUsage =
    radioValue(scope, 'current_estimation_type', {
      fuelType
    }) == 'usage';
  if (hasElectricityUsage) {
    const usage = parseInt(
      controlValue(scope, 'current_usage_estimated', {
        fuelType
      }),
      10
    );

    if (annual) {
      const utilityPeriod = getUtilityPeriod(scope, fuelType);
      if (utilityPeriod == 'monthly') {
        return usage * 12;
      }
    }
    return usage;
  }
}

export function getSupplier(scope, fuelType) {
  const control = scope.querySelector(
    `select${nameSelector('supplier', fuelType)}`
  );
  return (
    (control && control.value) || radioValue(scope, 'supplier', { fuelType })
  );
}

export function isUsageManualInput(scope, fuelType) {
  return radioValue(scope, 'current_usage_preset', { fuelType }) == 'actual';
}

export function getUtilityPeriod(scope, fuelType) {
  return controlValue(scope, 'utility_period', { fuelType });
}

export function hasPostcode(scope) {
  return controlValue(scope, 'postcode');
}

export function hasEconomy7(scope) {
  return booleanRadioValue(scope, 'economy7');
}

export function hasSameBillingAddress(scope) {
  return !!scope.querySelector(
    `${nameSelector('has_same_billing_address')}:checked`
  );
}

export function hasMedicalCondition(scope) {
  return !!scope.querySelector(
    // TODO: Update nameSelector to allow a path to be provided as first argument
    `${nameSelector(
      'terms_and_conditions_attributes][has_medical_condition'
    )}:checked`
  );
}

export function hasSmartMeter(scope) {
  return !!scope.querySelector(
    // TODO: Update nameSelector to allow a path to be provided as first argument
    `${nameSelector(
      'terms_and_conditions_attributes][smart_meters_installed]['
    )}:not([value=""]):checked`
  );
}

export function hasCoOperativeMembershipNumber(scope) {
  return !!scope.querySelector(
    `${nameSelector(
      'terms_and_conditions_attributes][has_co_operative_membedship_number'
    )}:checked`
  );
}

export function getSupplyAddress(scope) {
  return controlValue(
    scope,
    'addresses_attributes][present_attributes][known_address'
  );
}

function booleanRadioValue(
  scope,
  attributeName,
  { fuelType, trueValue = 'true' } = {}
) {
  return radioValue(scope, attributeName, { fuelType }) == trueValue;
}

function controlValue(scope, attributeName, { fuelType } = {}) {
  const control = scope.querySelector(nameSelector(attributeName, fuelType));
  if (control) {
    return control.value;
  } else {
    return getSubmissionData(
      attributeNameWithFuelType(attributeName, fuelType)
    );
  }
}

function radioValue(scope, attributeName, { fuelType } = {}) {
  const hasControl = scope.querySelector(nameSelector(attributeName, fuelType));
  if (hasControl) {
    const checkedEl = scope.querySelector(
      `${nameSelector(attributeName, fuelType)}:checked`
    );
    return checkedEl && checkedEl.value;
  } else {
    return getSubmissionData(
      attributeNameWithFuelType(attributeName, fuelType)
    );
  }
}



// WEBPACK FOOTER //
// ./app/webpack/javascripts/embed/form.js